import CardCommon from '@op/components/CardCommon';
import React, { useEffect, useRef, useState } from 'react';
import { channelMentionSentimentTabType, goToAISummary, mentionsSentimentTab, MentionsSentimentTabType } from '../constants';
import { Button, Col, Row, Segment } from 'tea-component';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

import ChannelChart from './Channel';
import LanguageChart from './Language';
import MetricsChart from './MetricsChart';
import MetricsList from './MetricsList';
import { gameInfoType } from '@op/types/common';
import { useHistory } from 'umi';
import './index.scss';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import useLanguage from '@op/hooks/useLanguage';
import AISummaryEvents from './AISummaryEvents';
import { DefaultImg } from '@op/constants/defaultImg';
import SvgIcon from '@home/components/SvgIcon';
import UserGuideOverview from '@op/components/UserGuide/overview';
import { keyMetricsShowCompetitorTips } from '@op/components/BubbleCommon';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';

export default function MentionsSentiment({
  filter,
  gameInfo,
  gameData,
  showRank,
  games,
  setDownloadVisible,
  onChange,
  wholeGameInfo,
  hideCompetitorRank,
  showSummaryEvents,
  summaryHasData,
  onSummaryHasDataChange,
}: {
  filter: any;
  gameInfo: gameInfoType;
  gameData: any;
  showRank: boolean;
  games: Array<any>;
  setDownloadVisible: (visible: boolean) => void;
  onChange: (value: any) => void;
  hideCompetitorRank?: boolean;
  wholeGameInfo?: any;
  showSummaryEvents?: boolean;
  summaryHasData?: boolean;
  onSummaryHasDataChange?: (hasData: boolean) => void;
}) {
  const { formatMessage } = useLanguage();
  const [value, setValue] = useState<MentionsSentimentTabType>('total');
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(true);
  const history = useHistory();
  const { goPage } = useDrilldownParams();
  const imgRangeRef = useRef<HTMLDivElement>(null);

  const channelType = channelMentionSentimentTabType[value] || 'total';
  // 默认进入页面时 loading=true，等 showSummaryEvents 确定后再决定
  const [summaryBtnLoading, setSummaryBtnLoading] = useState<boolean>(true);

  function handleClick(type: 'Channel' | 'Language') {
    history.push(`/opinion/Stats/${type}`);
  }

  // 按钮 loading 逻辑:
  // 1. 默认进入页面 / filter 变化: 立即 loading=true
  // 2. 1 秒后检查 showSummaryEvents=false → 停止 loading
  // 3. showSummaryEvents=true → 保持 loading，等 statsLoading=false 后停止
  useEffect(() => {
    setSummaryBtnLoading(true);
    const timer = setTimeout(() => {
      if (!showSummaryEvents) {
        setSummaryBtnLoading(false);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [showSummaryEvents, filter]);

  const leftFilters: any[] = [
    {
      key: 'metrics',
      element: (
        <Segment
          className="segment-light segment-font12"
          value={value}
          onChange={(value: MentionsSentimentTabType) => {
            setValue(value);
            buriedBtnPoint('KMMS_TypeTAB', value);
          }}
          options={mentionsSentimentTab(formatMessage)}
        />
      ),
    },
    {
      key: 'distance',
      show: !summaryBtnLoading,
      element: (
        <Button
          className="dbo-ai-summary-toggle-btn"
          onClick={() => summaryHasData ? setAiSummaryExpanded(!aiSummaryExpanded) : goToAISummary(history)}
        >
          <div className='dbo-ai-summary-toggle-btn__container'>
            <img
              src={DefaultImg.AI}
              alt=""
              className="dbo-ai-summary-toggle-btn__icon"
            />
            <span className="dbo-ai-summary-toggle-btn__text">
              {formatMessage(summaryHasData ? 'View AI Summary' : 'Get AI Summary')}
            </span>
            {
              summaryHasData ?
                (
                  <SvgIcon
                    type={"agent-point-top"}
                    className={`dbo-ai-summary-toggle-btn__arrow${aiSummaryExpanded ? '' : ' dbo-ai-summary-toggle-btn__arrow--collapsed'}`}
                  />
                )
                : (
                  <SvgIcon type="arrow-right" className="dbo-ai-summary-toggle-btn__arrow" />
                )
            }
          </div>
        </Button>
      ),
    }
  ];

  return (
    <CardCommon
      className="dbo-keymetrics-mentions-sentiment"
      title={formatMessage({ id: 'Mentions & Sentiment' })}
      contentCard
    >
      <FilterBoxCommon
        className="dbo-metrics-filter-box"
        left={leftFilters}
        right={[
          {
            key: 'singleCheckbox',
            valueKey: 'showRank',
            defaultValue: showRank,
            defaultParams: {
              text: 'Show ranking among competitors',
              tipsMap: keyMetricsShowCompetitorTips(() => {
                goPage({
                  path: '/opinion/Compare',
                  filter: filter || {},
                  // Compare 为特殊页面，走本地缓存
                  mode: EDrilldownMode.Local,
                });
              }),
              showIcon: true,
              placement: 'top'
            },
            show: !hideCompetitorRank
          },
          {
            key: 'download',
            className: 'dbo-performance-download',
            event: {
              onClick: () => {
                setDownloadVisible(true);
              },
            },
          },
        ]}
        onChange={(value, key: any) => {
          if (key === 'showRank') {
            onChange(value);
          }
        }}
      ></FilterBoxCommon>
      {
        !summaryBtnLoading && (
          <UserGuideOverview></UserGuideOverview>
        )
      }
      <div ref={imgRangeRef}>
        {showSummaryEvents && (
          <AISummaryEvents
            gameInfo={gameInfo}
            wholeGameInfo={wholeGameInfo}
            mode='events'
            expanded={aiSummaryExpanded}
            filter={filter}
            channelType={channelType}
            onHasDataChange={onSummaryHasDataChange}
            setSummaryBtnLoading={setSummaryBtnLoading}
          />
        )}
        <MetricsList
          tab={value}
          filter={filter}
          gameData={gameData}
          showRank={showRank}
          games={games}
          gameInfo={gameInfo}
        ></MetricsList>
        <MetricsChart
          tab={value}
          filter={filter}
          gameInfo={gameInfo}
        ></MetricsChart>
      </div>
      <Row>
        <Col span={12}>
          <ChannelChart
            tab={value}
            titleClickFunc={() => handleClick('Channel')}
            filter={filter}
            detailPage
          />
        </Col>
        <Col span={12}>
          <LanguageChart
            tab={value}
            titleClickFunc={() => handleClick('Language')}
            filter={filter}
            detailPage
          />
        </Col>
      </Row>
    </CardCommon>
  );
}
